import React, { useEffect, useState } from "react";

import { FaEdit, FaTimes, FaTrash, FaExclamationCircle } from "react-icons/fa";
import EventForm from "./EventForm";
import EventFeeAmountFields from "./EventFeeAmountFields";
import { getPlacementRuleLabel } from "../../../utility/placementRules";
import {
  amountsFromEvent,
  EVENT_FEES_LABEL,
  formatEventFeesSummary,
} from "../../../utility/eventFees";
import { PrimaryButton, SoftButton, IconButton } from "../../UI/DesignStandards";
import { useTournamentId } from "../../../context/TournamentRouteContext";

const EventCard = ({
  event,
  onSave,
  onDelete,
  onSaveFees,
  catalog = [],
  isLoggedIn,
  isPayoutFinalized,
}) => {
  const tournamentId = useTournamentId();
  const [isEditing, setIsEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [customizingFees, setCustomizingFees] = useState(false);
  const [feeAmounts, setFeeAmounts] = useState({});
  const [savingFees, setSavingFees] = useState(false);
  const feeSignature = (event.fees || [])
    .map((fee) => `${fee.feeName}:${fee.amount}`)
    .join("|");

  useEffect(() => {
    setFeeAmounts(amountsFromEvent(event, catalog));
  }, [event.id, feeSignature, catalog]);

  const handleSave = async (updatedEvent) => {
    await onSave(updatedEvent);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleDelete = () => {
    onDelete(event.id, event.isTemplate);
    setConfirmingDelete(false);
  };

  const handleSaveFees = async () => {
    if (!onSaveFees || isPayoutFinalized) return;
    setSavingFees(true);
    try {
      await onSaveFees(event, feeAmounts);
      setCustomizingFees(false);
    } finally {
      setSavingFees(false);
    }
  };

  const feeSummary = formatEventFeesSummary(event.fees);

  if (isEditing) {
    return (
      <div className="card-inset p-6 mb-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="heading-display text-lg">Edit Event</h3>
          <IconButton onClick={handleCancel} icon={FaTimes} variant="ghost" />
        </div>
        <EventForm
          tournamentId={tournamentId}
          initialData={event}
          onSubmit={handleSave}
          onCancel={handleCancel}
        />
      </div>
    );
  }

  return (
    <div className="card-inset p-4 lg:p-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div className="flex-1">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-3">
            <h3 className="font-display font-bold text-lg text-brand-charcoal">
              {event.eventName}
            </h3>
            {event.isPrimary && (
              <span className="inline-flex items-center bg-brand-charcoal text-brand-cream px-2 py-1 rounded-lg text-xs font-display font-bold self-start">
                Primary Event
              </span>
            )}
            {event.isTemplate && (
              <span className="inline-flex items-center bg-brand-cream text-brand-charcoal px-2 py-1 rounded-lg text-xs font-display font-bold border border-brand-charcoal/10 self-start">
                Per-Squad Event
              </span>
            )}
            {!event.isPrimary && !event.isTemplate && (
              <span className="inline-flex items-center bg-brand-cream text-brand-charcoal px-2 py-1 rounded-lg text-xs font-display font-bold border border-brand-charcoal/10 self-start">
                Tournament-Wide Event
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-brand-muted">
            {event.isPrimary && (
              <div>
                <span className="font-medium">Team Size:</span>{" "}
                {event.bowlerQuantity} bowlers
              </div>
            )}
            {event.isPrimary && typeof event.gamesPerEvent === "number" && (
              <div>
                <span className="font-medium">Games Per Event:</span>{" "}
                {event.gamesPerEvent}
              </div>
            )}
            {event.eventAbbreviation && (
              <div>
                <span className="font-medium">Abbreviation:</span>{" "}
                {event.eventAbbreviation}
              </div>
            )}
            {event.calculationTypeName && (
              <div>
                <span className="font-medium">Score Method:</span>{" "}
                {event.calculationTypeName}
              </div>
            )}
            <div>
              <span className="font-medium">Placement Rule:</span>{" "}
              {getPlacementRuleLabel(
                event.bowlerQuantity,
                event.maxSharedPlayers
              )}
            </div>
            {event.allEventConfig && (
              <>
                <div className="sm:col-span-2">
                  <span className="font-medium">Primary Events:</span>{" "}
                  {event.allEventConfig.primaryEvents &&
                  event.allEventConfig.primaryEvents.length > 0 ? (
                    <span>
                      {event.allEventConfig.primaryEvents
                        .map((pe) =>
                          pe.abbreviation
                            ? `${pe.name} (${pe.abbreviation})`
                            : pe.name
                        )
                        .join(", ")}
                    </span>
                  ) : (
                    <span className="text-brand-muted">None selected</span>
                  )}
                </div>
                <div>
                  <span className="font-medium">Secondary Rule:</span>{" "}
                  {event.allEventConfig.secondaryRuleType === "first_attempt"
                    ? "First Attempt"
                    : event.allEventConfig.secondaryRuleType}
                </div>
              </>
            )}
          </div>
        </div>

        {isLoggedIn && (
          <div className="flex flex-col sm:flex-row gap-2 lg:flex-col lg:gap-2">
            <SoftButton
              color="cream"
              onClick={() => setIsEditing(true)}
              disabled={isPayoutFinalized}
              className={
                isPayoutFinalized ? "opacity-50 cursor-not-allowed" : ""
              }
              title={
                isPayoutFinalized
                  ? "Payouts have been locked and cannot be changed"
                  : "Edit this event"
              }
            >
              <FaEdit className="mr-2 h-4 w-4" />
              Edit
            </SoftButton>
            {confirmingDelete ? (
              <div className="flex flex-col sm:flex-row gap-2">
                <span className="text-sm text-red-600 font-medium text-center sm:text-left">
                  Delete?
                </span>
                <div className="flex gap-2 justify-center sm:justify-start">
                  <SoftButton
                    color="red"
                    size="xs"
                    onClick={handleDelete}
                    disabled={isPayoutFinalized || event.teamCount > 0}
                    title={
                      isPayoutFinalized
                        ? "Payouts have been locked and cannot be changed"
                        : event.teamCount > 0
                        ? `Cannot delete event: ${event.teamCount} team${
                            event.teamCount !== 1 ? "s" : ""
                          } or bowler${
                            event.teamCount !== 1 ? "s" : ""
                          } are assigned. Remove all assignments first.`
                        : ""
                    }
                  >
                    Yes
                  </SoftButton>
                  <SoftButton
                    color="gray"
                    size="xs"
                    onClick={() => setConfirmingDelete(false)}
                    disabled={isPayoutFinalized}
                    title={
                      isPayoutFinalized
                        ? "Payouts have been locked and cannot be changed"
                        : ""
                    }
                  >
                    No
                  </SoftButton>
                </div>
              </div>
            ) : (
              <SoftButton
                color="red"
                onClick={() => setConfirmingDelete(true)}
                disabled={isPayoutFinalized || event.teamCount > 0}
                className={
                  isPayoutFinalized || event.teamCount > 0
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }
                title={
                  isPayoutFinalized
                    ? "Payouts have been locked and cannot be changed"
                    : event.teamCount > 0
                    ? `Cannot delete event: ${event.teamCount} team${
                        event.teamCount !== 1 ? "s" : ""
                      } or bowler${
                        event.teamCount !== 1 ? "s" : ""
                      } are assigned. Remove all assignments first.`
                    : "Delete this event"
                }
              >
                <FaTrash className="mr-2 h-4 w-4" />
                Delete
              </SoftButton>
            )}
          </div>
        )}
      </div>

      <div className="mt-4 pt-4 border-t border-brand-charcoal/10">
        {customizingFees ? (
          <div className="space-y-4">
            <EventFeeAmountFields
              title={EVENT_FEES_LABEL}
              fees={catalog}
              amounts={feeAmounts}
              onAmountChange={(feeId, amount) =>
                setFeeAmounts((prev) => ({ ...prev, [feeId]: amount }))
              }
              disabled={!isLoggedIn || isPayoutFinalized}
              events={[event]}
            />
            {isLoggedIn ? (
              <div className="flex justify-end gap-2">
                <SoftButton
                  color="cream"
                  onClick={() => {
                    setFeeAmounts(amountsFromEvent(event, catalog));
                    setCustomizingFees(false);
                  }}
                >
                  Cancel
                </SoftButton>
                <PrimaryButton
                  type="button"
                  onClick={handleSaveFees}
                  disabled={isPayoutFinalized}
                  loading={savingFees}
                >
                  Save fees
                </PrimaryButton>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            {feeSummary ? (
              <p className="text-sm text-brand-muted">
                <span className="font-medium text-brand-charcoal">
                  {EVENT_FEES_LABEL}:{" "}
                </span>
                {feeSummary}
              </p>
            ) : (
              <div
                className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-800 rounded-lg px-3 py-2"
                role="alert"
              >
                <FaExclamationCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm font-display font-bold">
                  Fees needed
                </span>
              </div>
            )}
            {isLoggedIn ? (
              <SoftButton
                color="cream"
                onClick={() => setCustomizingFees(true)}
                disabled={isPayoutFinalized}
                className={isPayoutFinalized ? "opacity-50 cursor-not-allowed" : ""}
              >
                Customize fees
              </SoftButton>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventCard;
